import React, { useState, useRef, useEffect, ForwardedRef, memo, forwardRef } from "react";
import { FieldErrors } from "react-hook-form";
// libs
import NextImage from "next/image";
import Input from "src/components/ui/input";
import { CSSProperties } from "styled-components";
// components
import { countries, ICountries } from "src/components/ui/phoneInput/countries";
// const
import { ICountry } from "src/constants/countries";
// hooks
import { useRouter } from "next/router";
// utils
import { getCountry } from "src/utils/countries";
// theme
import {
  Context,
  ButtonContainer,
  FlagContainer,
  ArrowContainer,
  InputContainer,
  CountryContainer,
  InputWrapper,
} from "./PhoneInput.styles";
import { Body } from "src/theme/typography";
import { colors } from "src/theme";
import { DropdownCard } from "../common";

interface IDefaultValue {
  code: string;
  phone: string;
}

interface IPhoneInput {
  errors?: FieldErrors;
  defaultValue?: IDefaultValue;
  styles?: CSSProperties;
  setCode: (value: string) => void;
}

const PhoneInput = (
  { errors, setCode, defaultValue, styles, ...rest }: IPhoneInput,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const dropdownOpenCountrys: any = useRef();
  const router = useRouter();

  let userCountry: ICountry = getCountry(router.locale, "locale") as ICountry;
  const formCountry = countries.filter((country) => country.code === defaultValue?.code)[0];

  const overrideCountryCode = router.query.country;
  let overrideCountry: typeof countries[0] | undefined;
  if (overrideCountryCode) {
    const matchedCountry = countries.find((country) => country.countryCode === overrideCountryCode);
    if (matchedCountry) {
      overrideCountry = matchedCountry;
    }
  }

  const [open, setOpen] = useState<boolean>(false);
  const [selectCountry, setSelectCountry] = useState<ICountries | null>(null);

  const handleOpen = () => setOpen(!open);

  useEffect(() => {
    if (defaultValue?.code && defaultValue?.phone) {
      setSelectCountry(formCountry);
      setCode(formCountry.countryCode);
    } else if (overrideCountry) {
      setSelectCountry(overrideCountry);
      setCode(overrideCountry.countryCode);
    } else {
      const initial = countries.filter((country) => country.name === userCountry.name)[0];
      setCode(initial.countryCode);
      setSelectCountry(initial);
    }
  }, [overrideCountry]);

  const closeDropdownInput = (e: MouseEvent) => {
    setOpen(false);
    if (e) {
      e.stopPropagation();
    }
  };

  const handleClickInputOutside = (event: MouseEvent): void => {
    if (!dropdownOpenCountrys.current?.contains(event.target)) {
      closeDropdownInput(event);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickInputOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickInputOutside);
    };
  }, [open]);

  const handleSelectCountryFlag = (country: ICountries) => {
    setCode(country.countryCode);
    setSelectCountry(country);
    setOpen(false);
  };

  if (!selectCountry) return null;

  return (
    <Context>
      <InputWrapper filled={errors} style={styles}>
        <ButtonContainer onClick={handleOpen}>
          <FlagContainer>
            <NextImage width={24} height={24} src={selectCountry?.flag} />
          </FlagContainer>
          <Body textColor={colors.greyDark} style={{ width: "100%" }}>
            {selectCountry?.code}
          </Body>
          <ArrowContainer>
            <NextImage width={12} height={7} src="/illustrations/arrow-down.svg" />
          </ArrowContainer>
        </ButtonContainer>
        {open && (
          <DropdownCard ref={dropdownOpenCountrys}>
            {countries.map((country) => (
              <CountryContainer key={country.id} onClick={() => handleSelectCountryFlag(country)}>
                <FlagContainer>
                  <NextImage width={24} height={24} src={country.flag} />
                </FlagContainer>
                <Body textColor={colors.greyDark}>{country?.name}</Body>
                <Body>{country?.code}</Body>
              </CountryContainer>
            ))}
          </DropdownCard>
        )}
        <InputContainer>
          <Input
            ref={ref}
            type="tel"
            style={{ height: "30px" }}
            defaultValue={defaultValue?.phone}
            {...rest}
          />
        </InputContainer>
      </InputWrapper>
    </Context>
  );
};

export default memo(forwardRef(PhoneInput));
